import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { shouldReanalyzeOnEdit } from '@trilirivos/story-text';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ArticleSources } from '../../components/Dialogs/ArticleSources';
import { isSafeHttpUrl } from '../../components/Dialogs/ArticleSources/helpers/isSafeHttpUrl';
import {
  computeDuplicateErrors,
  findDuplicateIndex,
  normalizeUrlForCompare,
} from '../../components/Dialogs/ArticleSources/helpers/normalizeUrlForComparison';
import { sanitizeInput } from '../../components/Dialogs/ArticleSources/helpers/sanitizeInput';
import { TextAnalysisResponse } from '../../components/Dialogs/TextAnalysisResponse';
import { EnlargeableImage } from '../../components/EnlargeableImage';
import ButtonComponent from '../../components/UI/Button/Button.component';
import TransitionAlert from '../../components/UI/TransitionAlert';
import { DRAFT } from '../../constants/draft.constants';
import { charCounter } from '../../constants/post.constants';
import { PRIVATE_URIS } from '../../constants/privateURIs.constants';
import { refetchAuthorFeedsAfterPublish } from '../../graphql/cache/authorFeedRefresh';
import { evictDraftFromCache } from '../../graphql/cache/draftCacheUpdates';
import { DELETE_DRAFT_MUTATION } from '../../graphql/resolvers/draftMutations/deleteDraft.gql';
import { GET_DRAFT_BY_ID } from '../../graphql/resolvers/draftQueries/getDraftById.gql';
import { ADD_POST_MUTATION } from '../../graphql/resolvers/postMutations/addPost.gql';
import { CREATE_ANALYSIS_MUTATION } from '../../graphql/resolvers/storyAnalysis/createAnalysis.gql';
import { DELETE_STORY_ANALYSIS_MUTATION } from '../../graphql/resolvers/storyAnalysis/mutations/deleteStoryAnalysis.gql';
import { formatDate } from '../../helpers/dateConverters';
import {
  hasLevelOneHeading,
  postPublishDisabledReason,
} from '../../helpers/publishGateReason';
import { sanitizeDraft } from '../../helpers/removeTypenameFromPayload';
import { renderLinkedText } from '../../helpers/renderLinkedText';
import { resolveNavRoute } from '../../helpers/resolveNavRoute';
import { seedAnalyzedSnapshot } from '../../helpers/seedAnalyzedSnapshot';
import { extractTextFromBlocks } from '../../helpers/textBlockExtractor';
import { triggerAnalysis } from '../../helpers/triggerAnalysis';
import { useContentDeletion } from '../../hooks/useContentDeletion';
import { useDrafts } from '../../hooks/useDrafts';
import { usePublishConstraints } from '../../hooks/usePublishConstraints';
import { useStoryAnalyzer } from '../../hooks/useStoryAnalyzer';
import type { AnalysisResponse } from '../../interfaces/analysisResponse';
import type {
  DraftInterfaceState,
  PostDraftInterface,
} from '../../interfaces/post.interface';
import type { SentimentResponse } from '../../interfaces/sentimentResponse';
import type { TextBlock } from '../../interfaces/textEditor.interface';
import type { UserState } from '../../interfaces/user.interface';
import { draftSlice } from '../../store/draft.store';
import { userSlice } from '../../store/user.store';
import { DeleteDraftConfirm } from './DeleteDraftConfirm';
import { DraftCharCounter } from './DraftCharCounter';
import '../../styles/post.scss';
import '../../styles/utilities.scss';
import './post-draft.scss';

/**
 * TRIL-620: extracted from the component body to MODULE SCOPE so the block rendering —
 * including the enlarge affordance — can be rendered by a test without standing up
 * the draft preview's Redux/Apollo/Router/Auth/Websocket providers.
 *
 * It was already a pure function of `block` (no closure state), so this is a move, not a
 * rewrite. Without it the wiring is untestable in practice and CI cannot tell whether this
 * surface renders an enlargeable image at all — which is exactly what review found: all
 * four web call sites could be reverted with the suite still fully green.
 */
export const renderPostDraftBlock = (block: TextBlock) => {
  switch (block.type) {
    case 'Heading': {
      const level = block.data?.level ?? 1;
      const HeadingTag = `h${level}` as React.ElementType;
      return (
        <HeadingTag className={'POST__HEADER'}>{block.data?.header}</HeadingTag>
      );
    }
    case 'Paragraph':
      return <p>{renderLinkedText(block.data?.paragraph)}</p>;
    case 'List':
      return block.data?.style === 'ordered' ? (
        <ol style={{ paddingLeft: '2rem', listStyleType: 'decimal' }}>
          {block.data?.items?.map((item: any, itemIndex: number) => (
            <li key={itemIndex - item.id}>{item.text}</li>
          ))}
        </ol>
      ) : (
        <ul style={{ paddingLeft: '2rem', listStyleType: 'disc' }}>
          {block.data?.items?.map((item: any, itemIndex: number) => (
            <li key={itemIndex - item.id}>{item.text}</li>
          ))}
        </ul>
      );
    case 'Image':
      return (
        <EnlargeableImage
          src={block.data?.file?.url as string}
          caption={block.data?.caption}
          fallbackAlt="Draft image"
        />
      );
    case 'Code':
      return (
        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <code>{block.data?.code}</code>
        </pre>
      );
    case 'Quote':
      return (
        <blockquote
          style={{
            borderLeft: '4px solid var(--accent-emeraude)',
            paddingLeft: '1rem',
            margin: '1rem 0',
            fontStyle: 'italic',
            color: 'var(--text-secondary)',
          }}
        >
          <p style={{ margin: 0 }}>{renderLinkedText(block.data?.paragraph)}</p>
          {block.data?.caption && (
            <p
              style={{
                margin: '0.5rem 0 0',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
              }}
            >
              — {block.data.caption}
            </p>
          )}
        </blockquote>
      );
    default:
      return null;
  }
};

const PostDraftComponent = ({
  userId,
  username,
  onEditDraft,
}: {
  userId: string;
  username: string;
  onEditDraft?: () => void;
}): React.ReactElement => {
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const params = useParams();
  const { draftId } = params;
  const { drafts } = useDrafts(userId);
  const {
    minStoryLength,
    error: constraintsError,
    refetch: refetchConstraints,
  } = usePublishConstraints();
  const { draft, reAnalyzeStory, analyzedSnapshot } = useSelector(
    (state: DraftInterfaceState) => state.draftStore,
  );
  // TRIL-604/605: `lastAnalyzedText` comes from the DETAIL query and only from there.
  //
  // The Redux draft is NOT a valid source. The Draft page fills it via
  // useDrafts().getDraftById(), which despite the name is `drafts.find(...)` over
  // GET_DRAFTS_QUERY — the LIST. The server deliberately omits the field from getDrafts
  // (it is the full body of every analysed draft; returning it per-row would multiply the
  // payload for a field only this screen uses). Reading it off the Redux draft therefore
  // yields undefined on every load, which silently disables TRIL-313's cosmetic-edit
  // suppression instead of fixing it — every analysed draft would flag stale on first edit.
  //
  // Sourcing it here also keeps it off the Redux draft object, so the save path's
  // re-dispatch from UPDATE_DRAFT_MUTATION (EditDraftFormRender) cannot wipe it.
  const { data: draftDetailData } = useQuery(GET_DRAFT_BY_ID, {
    variables: { id: draftId },
    skip: !draftId,
  });
  const persistedAnalyzedText: string | null =
    draftDetailData?.getDraftById?.lastAnalyzedText ?? null;
  const { user } = useSelector((state: UserState) => state.userStore);
  const [isDeleting, setIsDeleting] = React.useState(false);
  // TRIL-571 — deleting cascades to the S3 content, every textBlocksVersions object,
  // the stored analysis and embedded images, none of it recoverable. The button now
  // opens this confirm instead of firing the mutation.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [publishBlockMessage, setPublishBlockMessage] = React.useState<
    string | null
  >(null);
  const {
    PUBLISH_DRAFT_INIT,
    PUBLISH_DRAFT_SUCCESS,
    RESET_DRAFT_STATE,
    SET_RE_ANALYZE_ACTION,
    SET_LAST_ANALYZED_TEXT,
    SET_STORY_ANALYSIS,
    SET_LAST_PUBLISHED_TOPIC,
  } = draftSlice.actions;
  const { UPDATE_USER } = userSlice.actions;
  const { deleteAllDraftContent, isDeleting: isDeletingContent } =
    useContentDeletion();
  const [allDrafts, setAllDrafts] = React.useState<PostDraftInterface[]>([]);
  const [draftsArray, setDraftsArray] = React.useState<PostDraftInterface[]>(
    [],
  );
  // AI
  const [storyText, setStoryText] = React.useState('');
  const [showAnalysis, setShowAnalysis] = React.useState(false);
  const {
    analyzeStory,
    analysisJobId,
    getSentiment,
    isAnalyzing,
    analysisResult,
    testConnection,
    getTestStories,
    preprocessReply, // Now includes can_submit and rate_limit_info
    reanalyzeWithReplies, // Now includes safety_failures in response
    getUserReplyStats, // Check user's remaining replies
    getStorySafetyStats, // Check story's safety status
    error: useStoryAnalyzerError,
  } = useStoryAnalyzer();
  const [normalizedArray, setNormalizedArray] =
    React.useState<PostDraftInterface[]>();
  const [currentDraft, setCurrentDraft] = React.useState<PostDraftInterface>();
  const [connectionStatus, setConnectionStatus] = React.useState('checking');
  const [draftTextAnalysis, setDraftTextAnalysis] = React.useState<
    AnalysisResponse | undefined
  >(undefined);
  const [draftTextSentiment, setDraftTextSentiment] = React.useState<
    SentimentResponse | undefined
  >(undefined);
  const [showAnalysisModal, setShowAnalysisModal] = React.useState(false);
  const [stringifiedStoryForAnalysis, setStringifiedStoryForAnalysis] =
    React.useState<string | undefined>(undefined);
  const [reAnalyzeButtonLabel, setReAnalyzeButtonLabel] = React.useState(false);
  const [showLastAnalysis, setShowLastAnalysis] = React.useState(false);
  const [showSourcesModal, setShowSourcesModal] = React.useState(false);
  const [categoriesExist, setCategoriesExist] = React.useState(false);
  const [articleSourcesArray, setArticleSourcesArray] = React.useState<
    string[]
  >([]);
  const [errors, setErrors] = React.useState<Array<string | null>>(() =>
    Array(DRAFT.ADD_SOURCES_DIALOG.MAX_SOURCES).fill(null),
  );

  React.useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await testConnection();
      setConnectionStatus(isConnected ? 'connected' : 'disconnected');
    };
    checkConnection();
  }, [testConnection]);

  React.useEffect(() => {
    setStringifiedStoryForAnalysis(
      extractTextFromBlocks(draft?.textBlocks as TextBlock[]),
    );
  }, [draft?.textBlocks]);

  const hasLoadedTextRef = React.useRef(false);
  // TRIL-318: the exact text sent to the analyzer, captured at analysis-trigger
  // time. The snapshot must bind to this (not a re-read of draft.textBlocks at
  // persist-completion) — createAnalysis uses awaitRefetchQueries, so onCompleted
  // fires after a network round-trip during which the editor stays live; a
  // mid-persist edit would otherwise be absorbed into the snapshot and escape the
  // re-analysis gate.
  const analyzedTextRef = React.useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only react to textBlocks changes
  React.useEffect(() => {
    if (!draft?.textBlocks?.length) return;
    if (!hasLoadedTextRef.current) {
      hasLoadedTextRef.current = true;
      // TRIL-313: a draft analyzed in a PRIOR session loads with hasStoryAnalysis but no
      // in-session snapshot, so it has to be seeded or the first edit fails safe and
      // re-flags even a cosmetic change.
      //
      // TRIL-605: seeding it from the CURRENT draft — which is what this did — was a
      // correctness hole, not just an approximation. This slice is not persisted, so a
      // reload always lands here; whatever the draft says NOW became "the analysed text",
      // absorbing every edit made since the analysis. Analyze -> edit -> save -> reload
      // therefore read as fresh, and Publish unlocked with an analysis that no longer
      // described the text. Web has no analyzedAt usage anywhere, so nothing else caught it.
      //
      // TRIL-604 makes the real value available: the server now stores the body that was
      // actually sent to the analyzer and returns it on getDraftById. Seed from THAT.
      //
      // Absent stays null rather than falling back to the current text. Null means "cannot
      // prove unchanged" and costs one re-analysis; the fallback silently asserts the draft
      // is unchanged, which is the bug. Analyses written before TRIL-604 have no value, so
      // they take the safe branch by construction.
      if (draft?.hasStoryAnalysis || storyHasAnalysis) {
        dispatch(
          SET_LAST_ANALYZED_TEXT(
            seedAnalyzedSnapshot(draftDetailData?.getDraftById),
          ),
        );
      }
      return;
    }
    if (draft?.hasStoryAnalysis || storyHasAnalysis) {
      // TRIL-313: only flag the analysis stale when the edit changed the content
      // beyond cosmetic (whitespace / trailing punctuation). Cosmetic edits keep
      // Publish enabled instead of forcing a re-analysis. Fails safe: a missing
      // snapshot or any word change → re-analyze.
      dispatch(
        SET_RE_ANALYZE_ACTION(
          shouldReanalyzeOnEdit(
            extractTextFromBlocks(draft?.textBlocks as TextBlock[]),
            analyzedSnapshot,
          ),
        ),
      );
    }
  }, [draft?.textBlocks]);

  React.useEffect(() => {
    if (
      (!reAnalyzeStory && draftTextAnalysis?.plugin_results) ||
      draft?.hasStoryAnalysis
    ) {
      setShowLastAnalysis(true);
    } else if (reAnalyzeStory && draftTextAnalysis?.plugin_results) {
      setShowLastAnalysis(false);
    }
  }, [reAnalyzeStory, draftTextAnalysis]);

  const handleAnalyze = async () => {
    if (!reAnalyzeStory && (showLastAnalysis || draft?.hasStoryAnalysis)) {
      setShowAnalysisModal(true);
      return;
    }

    if (draftTextAnalysis && draftTextSentiment && !reAnalyzeStory) {
      setShowAnalysisModal(true);
      return;
    }

    const storyText = stringifiedStoryForAnalysis as string;
    // TRIL-318: bind the upcoming snapshot to the text actually analyzed.
    analyzedTextRef.current = storyText;

    try {
      setOpenBackdrop(true);
      const { analysis, sentiment } = await triggerAnalysis({
        storyText,
        articleSources: articleSourcesArray,
        verifySources: !!articleSourcesArray.length,
      });

      setDraftTextAnalysis(analysis as any);
      setDraftTextSentiment(sentiment as any);

      if (reAnalyzeStory) {
        setShowLastAnalysis(false);
      }
    } catch (err) {
      setOpenBackdrop(false);
      console.error('Analysis failed:', err);
    } finally {
      setOpenBackdrop(false);
      setShowAnalysisModal(true);
    }
  };

  const [storyHasAnalysis, setStoryHasAnalysis] = React.useState(
    draft?.hasStoryAnalysis,
  );
  React.useEffect(() => {
    if (draft?.storyAnalysis?.length && draft?.storyAnalysis?.length > 0) {
      const { sentiment } = draft?.storyAnalysis[0] as any;
      setDraftTextSentiment(sentiment);
    }
  }, [draft?.storyAnalysis]);

  React.useEffect(() => {
    if (draft?.hasStoryAnalysis) {
      setStoryHasAnalysis(true);
    }
  }, [draft?.hasStoryAnalysis]);

  const loadTestStory = async () => {
    const testStories = await getTestStories();
    if (testStories.length > 0) {
      setStoryText(testStories[0].text);
    }
  };

  const handleEdit = () => console.log('handleEdit');

  function normalizeToArray<T>(data: T[] | Record<any, any>): T[] {
    if (Array.isArray(data)) {
      return data; // Already an array, return as is
    } else if (typeof data === 'object') {
      return [data]; // Wrap the nested object in an array
    }
    throw new Error(
      'Invalid input type: Expected an array of objects or an object wrapped in another object.',
    );
  }

  React.useEffect(() => {
    if (drafts) {
      setAllDrafts(drafts);
      const draftsArr = normalizeToArray(drafts);
      setDraftsArray(draftsArr);
    }
  }, [drafts]);

  const ADD_POST_VARIABLES = {
    userId: '',
    textBlocks: [],
    status: 'published',
    categories: [],
    article: true,
    id: '',
  };

  const handleClickClose = async () => {
    setShowAnalysisModal(false);
  };

  // "Revise story" on a self-harm PROMOTION block (TRIL-274): the analysis was
  // never saved (hasStoryAnalysis stays false), but the result lingers in local
  // state and keeps the Analyze button on "Show Last Analysis" — which only
  // reopens the stale analysis and never re-dispatches. Discard the cached
  // analysis so the button returns to "Analyze" and the author can re-analyze
  // their revised content.
  const handleReviseStory = async () => {
    setDraftTextAnalysis(undefined);
    setDraftTextSentiment(undefined);
    setShowLastAnalysis(false);
    // TRIL-313: revise discards the analysis → no current snapshot, so the next
    // edit fails safe to re-analyze.
    dispatch(SET_LAST_ANALYZED_TEXT(null));
    dispatch(SET_RE_ANALYZE_ACTION(false));
    setShowAnalysisModal(false);
  };

  const STORY_ANALYSIS_VARIABLES = {
    userId: userId,
    draftId,
    published: false,
    analysis: [],
    sources: articleSourcesArray,
  };

  const [
    createAnalysis,
    {
      data: createAnalysisData,
      loading: createAnalysisLoading,
      error: createAnalysisError,
    },
  ] = useMutation(CREATE_ANALYSIS_MUTATION, {
    variables: {
      input: STORY_ANALYSIS_VARIABLES,
    },
    refetchQueries: [{ query: GET_DRAFT_BY_ID, variables: { id: draftId } }],
    awaitRefetchQueries: true,
    onCompleted: ({ createAnalysis }) => {
      setStoryHasAnalysis(true);
      dispatch(SET_STORY_ANALYSIS({ analysis: createAnalysis.analysis }));
      // TRIL-313: snapshot the text that was just analyzed so later cosmetic-only
      // edits don't re-flag the analysis as stale.
      // TRIL-318: snapshot the text actually sent to the analyzer (captured at
      // trigger time), not a re-read of draft.textBlocks here — they diverge if
      // the draft was edited during the persist+refetch round-trip.
      // TRIL-605: no fallback to the current draft text. `analyzedTextRef` is null only
      // when handleAnalyze short-circuited without running an analysis, and in that case
      // the client also sends null to the server — so substituting current text here would
      // seed a snapshot the server does not have, reinstating exactly this ticket's bug for
      // the rest of the session. Null costs one re-analysis and cannot assert a falsehood.
      dispatch(SET_LAST_ANALYZED_TEXT(analyzedTextRef.current));
      dispatch(SET_RE_ANALYZE_ACTION(false));
    },
    onError: (ex) => {
      console.log('CREATE_ANALYSIS_MUTATION error =>', ex);
    },
  });

  const [deleteStoryAnalysis] = useMutation(DELETE_STORY_ANALYSIS_MUTATION, {
    variables: {
      id: draft?.id,
      userId: userId,
    },
    onCompleted: ({ deleteDraftAnalysis }) => {
      console.log('deleteDraftAnalysis completed', deleteDraftAnalysis);
    },
    onError: (ex) => {
      console.log('DELETE_DRAFT_ANALYSIS_MUTATION error =>', ex);
    },
  });

  const [deleteDraft] = useMutation(DELETE_DRAFT_MUTATION, {
    variables: {
      id: draft?.id,
      username: username,
      userId: userId,
    },
    onCompleted: ({ deleteDraft }) => {
      const updatedDrafts = drafts.length
        ? drafts.filter((d) => d.id !== draft?.id)
        : [];
      const updatedUserObject = {
        ...user,
        drafts: updatedDrafts,
      };
      dispatch(UPDATE_USER(updatedUserObject));
      dispatch(RESET_DRAFT_STATE(true));
    },
    update(cache) {
      if (draft?.id) {
        evictDraftFromCache(cache, draft.id);
      }
    },
    onError: (ex) => {
      console.log('DELETE_DRAFT_MUTATION error =>', ex);
    },
  });

  const [addPost, { data, loading, error }] = useMutation(ADD_POST_MUTATION, {
    variables: {
      input: {
        ...ADD_POST_VARIABLES,
      },
    },
    update(cache, { data }) {
      const newPost = data?.addPost;
      if (!newPost) return;
      const newRef = cache.identify(newPost);
      if (!newRef) return;

      // Profile feed: prepend the new post into ONLY the publishing user's
      // getUserPosts cache bucket. cache.modify(getUserPosts) iterates over
      // every cached args bucket (every profile the viewer has loaded); without
      // filtering by storeFieldName, the prepend would pollute other users'
      // cached profile lists until their next refetch. There is no field
      // policy on getUserPosts to dedup the write either.
      cache.modify({
        fields: {
          getUserPosts(existing: any, { storeFieldName }) {
            if (!Array.isArray(existing)) return existing;
            const argsMatch = storeFieldName.match(/getUserPosts\((\{.*\})\)/);
            if (!argsMatch) return existing;
            try {
              const args = JSON.parse(argsMatch[1]);
              if (args.username !== username) return existing;
            } catch {
              return existing;
            }
            return [{ __ref: newRef }, ...existing];
          },
        },
      });

      // Recommendation feed: evict + gc so the server's ranked algorithm
      // places the post in the correct position on next refetch.
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'getUserFeed' });
      cache.gc();
    },
    // TRIL-319: the post-publish redirect lives solely in handlePublishDraft's
    // .then (single, canonical resolveNavRoute(FEED) — runs after the success
    // cleanup). The old onCompleted navigate was a redundant, premature copy.
    onError: (ex) => {
      console.log('ADD_POST_MUTATION error =>', ex);
    },
  });

  React.useEffect(() => {
    if ((draft as PostDraftInterface).categories?.length > 0) {
      setCategoriesExist(true);
    } else {
      setCategoriesExist(false);
    }
  }, [draft]);

  const renderBlock = renderPostDraftBlock;

  const handleDeleteDraft = async () => {
    setConfirmDeleteOpen(false);
    setIsDeleting(true);
    try {
      await deleteDraft({
        variables: {
          userId: userId,
          id: (draft as PostDraftInterface).id,
          username: username,
        },
      });

      const updatedDrafts = drafts.length
        ? drafts.filter((d) => d.id !== draft?.id)
        : [];
      const updatedUserObject = {
        ...user,
        drafts: updatedDrafts,
      };
      dispatch(UPDATE_USER(updatedUserObject));
      dispatch(RESET_DRAFT_STATE(true));

      // Delete S3 content (images + content folder)
      const results = await deleteAllDraftContent(
        draft?.textBlocks as TextBlock[],
        user.id,
        (draft as PostDraftInterface).id,
      );

      // Log image deletion results
      if (results.imageResults.success.length > 0) {
        console.log(
          'Successfully deleted images from S3:',
          results.imageResults.success.length,
        );
      }

      if (results.imageResults.failed.length > 0) {
        console.warn(
          'Some images failed to delete from S3:',
          results.imageResults.failed,
        );
      }

      // Log content folder deletion results
      if (results.contentResults.success) {
        console.log(
          'Successfully deleted content folder from S3:',
          results.contentResults.key,
        );
      }

      if (results.contentResults.error) {
        console.warn('Failed to delete content folder from S3:', {
          key: results.contentResults.key,
          error: results.contentResults.error,
        });
      }

      navigate(PRIVATE_URIS.DRAFTS.replace(':username', username));
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAcceptAnalysis = async () => {
    const CREATE_STORY_ANALYSIS_VARIABLES = {
      draftId,
      userId: userId,
      published: false,
      analysis: [
        {
          version: 0,
          storyAnalyzer: draftTextAnalysis,
          sentiment: draftTextSentiment,
          sources: articleSourcesArray,
        },
      ],
      // TRIL-604: persist the text that was actually sent to the analyzer, so a later
      // session can tell a cosmetic edit from a real one without re-deriving it. This is
      // the TRIL-318 value (captured at trigger time), NOT a re-read of draft.textBlocks --
      // those diverge when the draft is edited during the analyze round-trip, and the
      // difference is exactly what this field has to capture.
      lastAnalyzedText: analyzedTextRef.current,
    };

    // TRIL-641: do NOT swallow this. `.catch(e => console.log(e))` is why a schema mismatch went
    // unnoticed for weeks — `service_errors` (which the analyzer emits only when a plugin fails)
    // was undeclared on AnalysisResponseInput, so GraphQL rejected the whole variable at coercion
    // and this line quietly discarded the ENTIRE analysis. The analyzer had degraded gracefully;
    // the app turned that into total data loss with no signal anywhere.
    //
    // The same raw pass-through exists in PostReplyDraft.component.tsx:531, which hit the identical
    // coercion rejection — the schema fix covers both, since they share AnalysisResponseInput.
    //
    // console.error at minimum, so a future mismatch is visible rather than lost in an info-level
    // log nobody reads.
    //
    // NOT surfaced to the author, and that is a SCOPE choice rather than a technical blocker — an
    // earlier draft of this comment claimed the bare un-awaited onClick prevented it, which is
    // wrong: this useMutation already exposes `createAnalysisError` and already distinguishes
    // success via onCompleted, and EditRewrite/index.tsx:215-231 wraps the same modal's accept
    // handler in try/catch + setActionError. What is missing is an error surface ON the modal plus
    // retry/dismiss behaviour — real UX work that belongs in its own ticket, not smuggled into a
    // schema fix. Tracked on TRIL-641.
    return await createAnalysis({
      variables: {
        input: {
          ...CREATE_STORY_ANALYSIS_VARIABLES,
        },
      },
    }).catch((e: unknown) => {
      console.error(
        'createAnalysis failed — the analysis was NOT persisted',
        e,
      );
      return undefined;
    });
  };

  React.useEffect(() => {
    setNormalizedArray(normalizeToArray(drafts));
  }, [drafts]);

  React.useEffect(() => {
    setCurrentDraft(
      normalizedArray?.find((d) => d.id === (draft as PostDraftInterface)?.id),
    );
  }, [normalizedArray]);

  const handleChange = (index: number, value: string) => {
    // sanitize on the way in (keeps state clean)
    console.log(index, value);
    const sanitized = sanitizeInput(value);
    setArticleSourcesArray((prev) => {
      const next = [...prev];
      next[index] = sanitized;
      return next;
    });

    // As user types, clear “duplicate” messages and recompute duplicates gently
    setErrors((prev) =>
      computeDuplicateErrors(
        // use the soon-to-be state (sanitized inserted)
        articleSourcesArray.map((v, i) => (i === index ? sanitized : v)),
        // keep any non-duplicate errors they already had
        prev,
      ),
    );
  };

  const handleBlur = (index: number, e: any) => {
    console.log('BLUR', index, e);
    const v = e.target.value.trim();
    // empty is allowed while typing; clear error
    if (!v) {
      console.log('!v');
      // return setErrors(e => (e[index] = DRAFT.ADD_SOURCES_DIALOG.ERROR.INVALID_URL, [...e]));
      setErrors((prev) => {
        const next = [...prev];
        // @ts-ignore
        next[index] = null;
        return next;
      });
      return;
    }

    // Must start with http(s) and pass safety validator
    const startsWithHttp = /^https?:\/\//i.test(v);
    if (!startsWithHttp || !isSafeHttpUrl(v)) {
      setErrors((prev) => {
        const next = [...prev];
        next[index] = DRAFT.ADD_SOURCES_DIALOG.ERROR.INVALID_URL;
        return next;
      });
      return;
    }

    // Duplicate check
    const values = articleSourcesArray.map((s, i) => (i === index ? v : s));
    const dup = findDuplicateIndex(values, index);
    setErrors((prev) => {
      const next = [...prev];
      // @ts-ignore
      next[index] = dup !== null ? `Duplicate of Source #${dup + 1}` : null;
      return next;
    });

    const ok = isSafeHttpUrl(v);
    // setErrors(e => ((e as any)[index] = ok ? null : DRAFT.ADD_SOURCES_DIALOG.ERROR.INVALID_URL, [...e]));
  };

  // Trim & keep non-empty (still raw strings; validation happens on submit)
  const cleanedSources = React.useMemo(
    () => articleSourcesArray.map((s) => s.trim()).filter(Boolean),
    [articleSourcesArray],
  );

  const onSubmitArticleSources = () => {
    const uniques = new Set<string>();
    const safe: string[] = [];
    for (const s of articleSourcesArray) {
      const v = s.trim();
      if (!v) continue;
      if (!isSafeHttpUrl(v)) continue;
      const canon = normalizeUrlForCompare(v);
      if (!canon) continue;
      if (uniques.has(canon)) continue;
      uniques.add(canon);
      safe.push(v); // keep original (or push canon if you prefer)
    }
    //
    // // If you prefer to hard-fail when any invalid is present, swap with Option B below.
    handleAddSources(safe);
  };

  const handleAddSources = (values: string[]) => {
    setArticleSourcesArray(values);
    setShowLastAnalysis(false);
  };

  const handleOpenSourcesModal = async () => {
    setShowSourcesModal(true);
  };
  const handleCloseSourcesModal = async () => {
    setShowSourcesModal(false);
  };
  const onClose = async () => {
    await handleCloseSourcesModal();
  };

  // Publishing a draft
  const handlePublishDraft = React.useCallback(async () => {
    dispatch(PUBLISH_DRAFT_INIT(true));

    if (!currentDraft) {
      console.error('Draft not found or state is stale.');
      return;
    }

    try {
      // TRIL-319: block the UI with the publishing spinner until the mutation
      // resolves (the success redirect then unmounts the editor). Prevents
      // double-submit / navigate-away mid-publish.
      setOpenBackdrop(true);
      const { textBlocks, textBlocksVersions, ...restOfDraft } =
        draft as PostDraftInterface;
      const sanitizeTextBlocks = () =>
        textBlocks.map((textBlock) => {
          let textBlockObj = {};
          // @ts-ignore
          const { __typename, items, ...restOfTextBlock } = textBlock.data;
          textBlockObj = {
            ...restOfTextBlock,
            data: {
              ...textBlock.data,
              items: [],
            },
          };
          if (items) {
            // @ts-ignore
            items.map((item: any, index: number) => {
              const { __typename, ...restOfItem } = item;
              return (textBlockObj = {
                ...textBlockObj,
                data: {
                  ...textBlock.data,
                  items: restOfItem,
                },
              });
            });
          }
          return textBlockObj;
        });

      const sanitizedTextBlocks = sanitizeTextBlocks();

      // Updated payload for addPost mutation
      const addPostPayload = {
        id: currentDraft.id, // Pass the draft ID - this is crucial!
        textBlocks: currentDraft.textBlocks, // need to first sanitize and remove the __typename property
        status: 'published',
        categories: currentDraft.categories,
        userId,
        username,
        article: true,
      };

      const sanitizedAddPostPayload = sanitizeDraft(addPostPayload);
      dispatch(PUBLISH_DRAFT_INIT(false));
      // Evict getUserPosts so the /posts page refetches on next mount.
      // Replaces the legacy REFRESH_POSTS Redux flag (TRIL-203).
      apolloClient.cache.evict({ fieldName: 'getUserPosts' });
      apolloClient.cache.gc();
      dispatch(PUBLISH_DRAFT_SUCCESS(true));

      // comment out this line to determine which .then issues the navigate
      // navigate(PRIVATE_URIS.POSTS.replace(':username', username));

      return await addPost({
        variables: {
          input: {
            ...addPostPayload,
            textBlocks: sanitizedAddPostPayload.textBlocks,
          },
        },
      }).then((result) => {
        setStoryText('');
        setShowAnalysis(false);
        // TRIL-116 Plan C: surface the post-publish context prompt globally.
        // primary_topic from storyAnalysis is generated async, so we fall back
        // to the just-published post's first category (Interests enum key).
        const publishedCategory =
          result?.data?.addPost?.categories?.[0] ??
          currentDraft?.categories?.[0] ??
          null;
        if (publishedCategory) {
          dispatch(SET_LAST_PUBLISHED_TOPIC(publishedCategory));
        }
        // TRIL-500: force the author's feed to refresh on the post-publish redirect (the feed
        // can stay mounted, so cache eviction alone doesn't re-run it — see helper for detail).
        refetchAuthorFeedsAfterPublish(apolloClient);
        // TRIL-319: land the author on their feed (post at top), not drafts —
        // the canonical route helper (the bare '/feed' literal is broken).
        navigate(resolveNavRoute(PRIVATE_URIS.FEED, username));
      });
    } catch (err: any) {
      const code =
        err?.graphQLErrors?.[0]?.extensions?.code ??
        err?.cause?.extensions?.code ??
        err?.extensions?.code;
      if (code === 'SELF_HARM_PROMOTION') {
        const message =
          err?.graphQLErrors?.[0]?.message ??
          err?.message ??
          "This story can't be published.";
        setPublishBlockMessage(message);
      } else {
        console.error('Error publishing draft:', err);
      }
      // TRIL-319: clear the publishing spinner so the block message is usable.
      setOpenBackdrop(false);
      dispatch(PUBLISH_DRAFT_INIT(false));
    }
  }, [
    drafts,
    normalizedArray,
    currentDraft,
    draft,
    allDrafts,
    user,
    userId,
    username,
    navigate,
  ]);

  const disableAnalyzeButton = isAnalyzing
    ? DRAFT.ANALYZING
    : reAnalyzeStory
      ? 'Re-Analyze'
      : showLastAnalysis || draft?.hasStoryAnalysis
        ? DRAFT.SHOW_LAST_ANALYSIS
        : DRAFT.ANALYZE;

  // TRIL-312: explain why Publish is disabled so the (correct) stale-analysis
  // gate doesn't read as a broken button. Mirrors the disable
  // condition on the Publish button below; null when Publish is enabled (or when
  // disabled only because analysis is in flight — the button already shows that).
  // TRIL-407: content must be long enough to analyze (posts + replies). Measured
  // on the same flattened text the analyzer receives; the server enforces it too.
  const storyLength = (stringifiedStoryForAnalysis as string)?.length ?? 0;
  // TRIL-420: floor comes from the server via usePublishConstraints (null while loading).
  const min = minStoryLength; // number | null
  const belowMinLength = min !== null && storyLength < min;
  const publishDisabledReason = postPublishDisabledReason(
    isAnalyzing,
    constraintsError,
    min,
    storyLength,
    categoriesExist,
    !!reAnalyzeStory,
    !!draft?.hasStoryAnalysis,
    hasLevelOneHeading(draft?.textBlocks as TextBlock[] | undefined),
  );

  // Single source of truth for the Publish gate: disabled iff analysis is in
  // flight or there's a stated reason. (isAnalyzing has no message, so it's OR'd
  // in separately.) The button's `disabled` derives from this — no duplicated
  // condition list to keep in sync.
  const publishDisabled = isAnalyzing || publishDisabledReason !== null;

  return (
    <>
      <div className="Container">
        <div>
          <div
            className={'DRAFT__ACTIONS__BAR'}
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '1rem',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {onEditDraft && (
              <ButtonComponent
                iconButton
                size="sm"
                variant={'ghost'}
                onClick={onEditDraft}
                className={'BUTTON_EDIT__DRAFT'}
              >
                <Pencil size={18} />
              </ButtonComponent>
            )}

            <ButtonComponent
              size="sm"
              variant={'outline'}
              type={'submit'}
              onClick={handleAnalyze}
              disabled={isAnalyzing || belowMinLength}
              className={'BUTTON__ANALYZE__DRAFT'}
            >
              {disableAnalyzeButton}
            </ButtonComponent>

            <ButtonComponent
              size="sm"
              variant={'outline'}
              type={'submit'}
              className={'BUTTON__ADD__SOURCES__DRAFT'}
              onClick={handleOpenSourcesModal}
              disabled={isAnalyzing}
            >
              {DRAFT.ADD_SOURCES}
            </ButtonComponent>

            <ButtonComponent
              size="sm"
              variant={'outline'}
              type={'submit'}
              className={'BUTTON__PUBLISH__DRAFT'}
              onClick={handlePublishDraft}
              disabled={publishDisabled}
            >
              {DRAFT.PUBLISH_DRAFT}
            </ButtonComponent>

            <ButtonComponent
              size="sm"
              disabled={isDeleting || isDeletingContent}
              variant={'outline'}
              className={'BUTTON__DELETE__DRAFT'}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              {isDeleting || isDeletingContent
                ? 'Deleting...'
                : DRAFT.DELETE_DRAFT}
            </ButtonComponent>

            {error && (
              <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                Error: {useStoryAnalyzerError}
              </div>
            )}
          </div>
          {publishDisabledReason && (
            <p className="DRAFT__PUBLISH__HINT">
              {publishDisabledReason}
              {constraintsError && (
                <button
                  type="button"
                  className="DRAFT__PUBLISH__HINT__RETRY"
                  onClick={() => refetchConstraints()}
                >
                  {charCounter.retry}
                </button>
              )}
            </p>
          )}
          <DraftCharCounter length={storyLength} min={minStoryLength} />
          <div className={'DRAFT__DATES'}>
            {draft?.createdAt && (
              <>
                <span>Created on: {formatDate(draft?.createdAt as Date)}</span>
              </>
            )}
            {draft?.createdAt &&
              draft?.updatedAt &&
              draft?.createdAt !== draft?.updatedAt && (
                <>
                  <span>
                    &nbsp;- Updated on: {formatDate(draft?.updatedAt as Date)}
                  </span>
                </>
              )}
          </div>

          {draft?.textBlocks?.map((block, index) => (
            <React.Fragment key={`${block.id}-${index}`}>
              {renderBlock(block)}
            </React.Fragment>
          ))}
          {showAnalysisModal && (
            <TextAnalysisResponse
              jobId={analysisJobId ?? ''}
              draftId={(draft as PostDraftInterface).id}
              userId={userId}
              hasStoryAnalysis={storyHasAnalysis as boolean}
              isReAnalyze={reAnalyzeStory}
              isModalOpen={showAnalysisModal}
              setShowAnalysisModal={setShowAnalysisModal}
              handleClickClose={handleClickClose}
              handleReviseStory={handleReviseStory}
              analysisResult={draftTextAnalysis as any}
              draftTextSentiment={draftTextSentiment as SentimentResponse}
              handleAcceptAnalysis={handleAcceptAnalysis as any}
              createAnalysisLoading={createAnalysisLoading}
              handleEdit={handleEdit as () => Promise<void>}
              draftStoryAnalyzerPluginResults={
                draft?.storyAnalysis !== undefined &&
                draft?.storyAnalysis?.length > 0
                  ? draft?.storyAnalysis[0]?.storyAnalyzer
                  : null
              }
            />
          )}
          {showSourcesModal && (
            <ArticleSources
              isSourcesModalOpen={showSourcesModal}
              handleCloseSourcesModal={onClose}
              onSubmitArticleSources={onSubmitArticleSources}
              handleInputChange={handleChange}
              articleSourcesArray={articleSourcesArray}
              errors={errors}
              handleBlur={handleBlur}
            />
          )}
        </div>
      </div>
      {publishBlockMessage && (
        <TransitionAlert
          open={true}
          severity="error"
          message={publishBlockMessage}
          handleOpenTransitionAlert={() => setPublishBlockMessage(null)}
        />
      )}
      {openBackdrop && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span className="spinner" role="status" aria-label="loading" />
        </div>
      )}
      <DeleteDraftConfirm
        open={confirmDeleteOpen}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteDraft}
      />
    </>
  );
};

export default PostDraftComponent;
